import React, { useEffect, useRef, useState } from 'react'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors, FieldProps, withFieldWrap } from '@/components/forms/common/FormBase'
import { Control, Controller, FieldValues, Path, useForm } from 'react-hook-form'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { useModal } from '@/components/modal/Modal'
import { formatDate } from '@/common/time'
import { isDesktopScreen, isLargeScreen, useResponsiveChange } from '@/common/device'
import { BackButton } from '@/components/layout/NavBar'
import { Roles } from '@/services/repository/users'
import { useSession } from '@/hooks/useSession'
import { Translates } from '@/services/repository/i18n'
import { Spinner } from '@/components/spinner'

export interface SimpleUser {
  id: string
  email: string
  username: string
  createdAt: Date
}

export interface UserRow {
  id: string
  username: string
}

export const SearchUsersPopup = ({ services, session, onClose, onSelect }: {
  services: Services,
  session: CurrentSession,
  onClose: () => void,
  onSelect: (user: UserRow) => void
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)

  if (!session || !services) {
    return <Spinner />
  }

  const tableParams: SearchTableParams<SimpleUser> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUser>> => {
      return services.session.fetchWithToken(`/api/superadmin/users/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    header: () => <>
      <div className='hcol desktop-only'>{session.translates.for('elements.table.users.createdAt')}</div>
      <div className='hcol desktop-only'>{session.translates.for('elements.table.users.email')}</div>
      <div className='hcol'>{session.translates.for('elements.table.users.username')}</div>
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col desktop-only'>{formatDate(new Date(row.createdAt))}</div>
      <div className='col desktop-only'>{row.email}</div>
      <div className='col'>{row.username}</div>
      <div className='col' onClick={() => {
        onClose()
        onSelect(row)
      }}>
        <i className='svgfont-next' />
      </div>
    </>,
    translates: session.translates
  }

  return <div>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </div>
}

export const CommonUsernameInput = (props: {
  fieldName: string
  formName: string
  translates: Translates
  value: string
  setValue: (v: string) => void
  readOnly?: boolean
  session: CurrentSession
  services: Services
}) => {
  const { openModal } = useModal()
  if (props.session !== undefined && props.session.user.role === Roles.SuperAdmin) {
    return <div className='des'>
      <div className="code-text-fild">
        <input
          type="text"
          id={props.fieldName}
          value={props.value}
          name={props.fieldName}
          readOnly={props.readOnly}
          onChange={(e) => props.setValue(e.target.value)}
          placeholder={props.translates.for(`inputs.username.placeholder`)}
        />
        <div className="toolbox"
          onClick={props.readOnly ? undefined : () => openModal((popupprops) => SearchUsersPopup({
            ...popupprops, services: props.services, session: props.session, onSelect: (user) => {
              props.setValue(user.username)
            }
          }))}>
          <div className="icon-button">
            <i className="svgfont-user"></i>
          </div>
        </div>
      </div>
    </div>

  }
  return <input
    type='text'
    id={props.fieldName}
    readOnly={props.readOnly}
    value={props.value}
    name={props.fieldName}
    onChange={(e) => props.setValue(e.target.value)}
    placeholder={props.translates.for(`inputs.username.placeholder`)} 
  />
}

// export const ControlledReactUsernameInput = ({
//   name,
//   control,
//   formName,
//   form,
//   translates,
//   readOnly,
//   session,
//   services,
//   required,
// }: {
//   name: string
//   control: Control<any>
//   formName: string
//   form: string
//   translates: Translates
//   readOnly: boolean
//   session: CurrentSession
//   services: Services
//   required: boolean
// }) => (
//   <Controller
//     name={name}
//     control={control}
//     rules={{
//       required: required ? translates.for(`forms.${formName}.errors.fields.${name}.required`) : undefined,
//     }}
//     render={({
//       field: { value, onChange },
//     }) => (
//       <CommonUsernameInput
//         fieldName={name}
//         formName={formName}
//         translates={translates}
//         value={value}
//         setValue={onChange}
//         readOnly={readOnly}
//         session={session}
//         services={services}
//       />
//     )}
//   />
// );

export const UsernameInput = <T extends FieldValues>(props: FieldProps<T>) => {
  return <Controller
    name={props.fieldName}
    control={props.form.control}
    rules={{
      required: props.required ? props.translates.for(`forms.${props.formName}.errors.fields.${props.fieldName}.required`) : undefined,
    }}
    render={({
      field: { value, onChange },
    }) => {
      const session = useSession(props.services)
      if (!session) {
        return <Spinner />
      }
      return <CommonUsernameInput
        fieldName={props.fieldName}
        formName={props.formName}
        translates={props.translates}
        value={value}
        setValue={onChange}
        readOnly={props.readOnly}
        session={session}
        services={props.services}
      />
    }}
  />
}

export const usernameField = <T extends FieldValues>() => withFieldWrap<T>(UsernameInput<T>)